'use client';

import {
  motion,
  useInView,
  useScroll,
  useTransform,
  type MotionValue,
} from 'motion/react';
import Image, { type StaticImageData } from 'next/image';
import { useEffect, useRef } from 'react';

import { MAIN_XL_MAX_QUERY } from '@/constants/main';
import { useLiteMode } from '@/hooks/useLiteMode.hook';
import { useMediaQuery } from '@/hooks/useMediaQuery.hook';
import { cn } from '@lib/utils';

import { useAfterPageLoad } from './useAfterPageLoad.hook';

const INACTIVE = '#BEC2CC';
const ACTIVE = '#2B2E33';

function StoryLine({
  text,
  index,
  count,
  progress,
  isLiteMode,
}: {
  text: string;
  index: number;
  count: number;
  progress: MotionValue<number>;
  isLiteMode: boolean;
}) {
  const color = useTransform(
    progress,
    [index / count, (index + 1) / count],
    [INACTIVE, ACTIVE],
  );
  return (
    <motion.span
      style={{ color: isLiteMode ? ACTIVE : color }}
      className="block"
    >
      {text}
    </motion.span>
  );
}

interface StoryVideoProps {
  name: string;
  poster: StaticImageData;
  playing: boolean;
  /** 이때 비로소 영상 소스를 붙인다. */
  shouldLoad: boolean;
  /** 행별 모션 시작 지연(초) */
  floatDelay: number;
  isLiteMode: boolean;
  isMobile: boolean;
}

function StoryVideo({
  name,
  poster,
  playing,
  shouldLoad,
  floatDelay,
  isLiteMode,
  isMobile,
}: StoryVideoProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  // 모바일은 시안상 둥둥 뜨는 모션 없음
  const isFloating = playing && !isLiteMode && !isMobile;

  useEffect(() => {
    const video = videoRef.current;
    if (!video || !shouldLoad) return;
    // <source> 를 마운트 뒤에 붙이면 브라우저가 알아서 다시 고르지 않는다.
    // load() 로 한 번만 알린다 — 재호출하면 재생 위치가 처음으로 돌아간다.
    if (!video.currentSrc) video.load();
    if (playing) {
      // 자동재생이 차단되면 포스터를 유지한다.
      video.play().catch(() => {});
    } else {
      video.pause();
    }
  }, [shouldLoad, playing, isLiteMode]);

  return (
    <motion.div
      aria-hidden="true"
      animate={{ y: isFloating ? [0, -15, 0] : 0 }}
      transition={{
        duration: isFloating ? 3.967 : 0,
        ease: [0.45, 0, 0.55, 1],
        repeat: isFloating ? Infinity : 0,
        delay: isFloating ? floatDelay : 0,
      }}
      className="relative aspect-[17/10] w-full max-w-[340px] shrink-0 overflow-hidden rounded-[30px] border-2 border-solid border-[#DBDFE3] bg-white xl:aspect-[11/10] xl:max-w-[440px] xl:rounded-[60px] xl:border-[6px]"
    >
      {isLiteMode ? (
        <Image
          src={poster}
          alt=""
          fill
          sizes="(max-width: 480px) calc(100vw - 40px), 440px"
          className="object-cover"
        />
      ) : (
        <video
          ref={videoRef}
          aria-hidden="true"
          className="absolute inset-0 h-full w-full object-cover"
          muted
          loop
          playsInline
          preload="metadata"
          poster={poster.src}
        >
          {shouldLoad ? (
            <>
              <source src={`/videos/${name}.webm`} type="video/webm" />
              <source src={`/videos/${name}.mp4`} type="video/mp4" />
            </>
          ) : null}
        </video>
      )}
    </motion.div>
  );
}

export interface StoryRowProps {
  lines: string[];
  mobileLines?: string[];
  videoName: string;
  poster: StaticImageData;
  /** 텍스트를 영상 왼쪽에 배치한다. */
  reverse?: boolean;
  floatDelay?: number;
}

export default function StoryRow({
  lines,
  mobileLines = lines,
  videoName,
  poster,
  reverse = false,
  floatDelay = 0,
}: StoryRowProps) {
  const ref = useRef<HTMLDivElement>(null);
  const isLiteMode = useLiteMode();
  // 소스를 처음부터 붙여두면 Safari 가 preload="metadata" 를 무시하고 스크롤 0 에서
  // 3개를 통째로 받아 히어로 영상·LCP 와 대역폭을 다툰다. 그래서 첫 화면이 끝난 뒤로
  // 미룬다. 다만 스크롤이 먼저 닿으면(첫 화면이 아직 안 끝났어도) 그때 바로 받는다.
  const nearViewport = useInView(ref, { margin: '300px', once: true });
  const afterPageLoad = useAfterPageLoad();
  const shouldLoad = nearViewport || afterPageLoad;
  // 보이지 않는 줄까지 스크롤 모션값을 구독하지 않도록 한 벌만 마운트한다.
  const isMobile = useMediaQuery(MAIN_XL_MAX_QUERY);
  const visibleLines = isMobile ? mobileLines : lines;
  // 행 중앙이 화면 중앙에 닿기 전에 모든 줄의 색 채우기를 완료한다.
  const { scrollYProgress } = useScroll({
    target: ref,
    offset: ['start 85%', 'center 55%'],
  });
  const inView = useInView(ref);

  return (
    <div
      ref={ref}
      className={cn(
        'mx-auto flex max-w-[960px] flex-col items-center justify-center gap-5 px-5 py-10 xl:flex-row xl:gap-[60px] xl:py-[120px]',
        reverse && 'xl:flex-row-reverse',
      )}
    >
      <StoryVideo
        name={videoName}
        poster={poster}
        playing={inView}
        shouldLoad={shouldLoad}
        isLiteMode={isLiteMode}
        isMobile={isMobile}
        floatDelay={floatDelay}
      />
      <p className="w-full max-w-[420px] text-center text-[20px] font-semibold leading-7 tracking-[-0.336px] xl:shrink-0 xl:text-left xl:text-[36px] xl:font-medium xl:leading-[44px] xl:tracking-[-0.784px]">
        {visibleLines.map((line, index) => (
          <StoryLine
            key={line}
            text={line}
            index={index}
            count={visibleLines.length}
            progress={scrollYProgress}
            isLiteMode={isLiteMode}
          />
        ))}
      </p>
    </div>
  );
}
